import {useTranslation} from "react-i18next";
import {
    preselectSingleEntity,
    UserLegalEntitiesOrDealersInputGroup
} from "../../component/UserLegalEntitiesOrDealersInputGroup/UserLegalEntitiesOrDealersInputGroup";
import {Button, Grid, Paper, TextField} from "@mui/material";
import SectionHeader from "../../../../util/component/SectionHeader";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import * as Yup from 'yup';
import {usePaOrdersActions} from "../../service/usePaOrdersActions";
import {PaOrder} from "./constants/interfaces";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {CheckCircleRounded} from "@mui/icons-material";
import CustomModal from "../../component/CustomModal";

const PaOrdersCreateOrUpdatePage = () => {
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'paOrders'});
    const {orderId} = useParams();
    const isUpdate = !!orderId;
    const navigate = useNavigate();
    const [order, setOrder] = useState<PaOrder | null>(null);
    const {getOrderById, createOrder, updateOrder} = usePaOrdersActions();
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [openModal, setOpenModal] = useState<boolean>(false);

    const VALUE_WITHOUT_BLANKSPACE = /^[a-zA-Z0-9_]*$/;

    useEffect(() => {
        if (isUpdate && orderId) {
            getOrderById(orderId).then((ord: any) => {
                setOrder(ord);
            });
        }
    }, []);

    const retrieveInitialValues = () => {
        if (isUpdate && order !== null) {
            return {
                tenantId: order.tenantId ?? "",
                dealerId: order.dealerId ?? "",
                cig: order.cig ?? '',
                cup: order.cup ?? '',
                description: order.description ?? '',
                orderId: order.orderId ?? ''
            };
        } else {
            return {
                tenantId: "",
                dealerId: "",
                cig: '',
                cup: '',
                description: '',
                orderId: ''
            }
        }
    };

    useEffect(() => {
        reset(retrieveInitialValues())
    }, [order]);

    const validationSchema = Yup.object().shape({
        cig: Yup.string().matches(VALUE_WITHOUT_BLANKSPACE, {
            excludeEmptyString: true,
            message: t('requiredCheck')
        }).required(t('requiredCheck')),
        cup: Yup.string().nullable(),
        orderId: Yup.string().nullable(),
        description: Yup.string().nullable()
    });

    const {handleSubmit, control, watch, reset, setValue} = useForm({
        defaultValues: retrieveInitialValues(),
        resolver: yupResolver(validationSchema)
    });

    const onSubmit = async (data: any) => {
        if (isUpdate) {
            data ={...data, id:orderId, idBusinessConvention : order && order.idBusinessConvention ? parseInt(order.idBusinessConvention) : ''};
            let responseData = await updateOrder(data)
            showResult(responseData);
        } else {
            if (globalData.user.legalEntities.length === 1) {
                data = {...data, tenantId: globalData.user.legalEntities[0].name}
            }
            if (globalData.user.dealers.length === 1) {
                data = {...data, dealerId: globalData.user.dealers[0].name}
            }
            let responseData = await createOrder(data)
            
            showResult(responseData);
        }
    }

    const showResult = (data: any) => {
        if (data && data.status === 'OK') {
            // show modal with confirm
            setOpenModal(true);
        } else {
            // show snackbar with error
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`errors.${data.message}`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    }

    useEffect(() => {
        preselectSingleEntity(globalData, setValue);
    }, [globalData, setValue]);
    return (
        <>
            <SectionHeader
                title={t(isUpdate ? 'updateHeader.title' : 'createHeader.title')}
                subtitle={t(isUpdate ? 'updateHeader.subTitle' : 'createHeader.subTitle')}/>
            <Paper elevation={3} sx={{padding: "16px", margin: "18px"}}>
                <form onSubmit={handleSubmit(data => {
                    onSubmit(data);
                }, errors => console.log(errors))} noValidate>
                    <Grid container rowSpacing={3} columnSpacing={3} alignItems={'flex-end'}>
                        <UserLegalEntitiesOrDealersInputGroup control={control} disabled={isUpdate}/>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"cig"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label={t('cigNumber')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        inputProps={{
                                            maxLength: 15
                                        }}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"cup"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        label={t('cupNumber')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        disabled={watch('cig')===''}
                                        inputProps={{
                                            maxLength: 15
                                        }}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"orderId"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        label={t('orderId')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        disabled={watch('cig')===''}
                                        inputProps={{
                                            maxLength: 20
                                        }}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"description"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        label={t('description')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        disabled={watch('cig')===''}
                                    />
                                )}/>
                        </Grid>
                    </Grid>
                    <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                        <Grid container item xs={12} rowSpacing={1} columnSpacing={3} justifyContent={"flex-end"}>
                            <Grid item>
                                <Button variant={"outlined"} color={"accent"}
                                        onClick={() => {
                                            navigate("/salesmarketing/paorderslist")
                                        }}>
                                    {t('buttons.cancel')}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button color={"accent"}
                                        type="submit">{t('buttons.confirm')}</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <CustomModal title={isUpdate ? t('modal.updateSuccess') : t('modal.createSuccess')}
                         cancelButton={t('buttons.cancel')}
                         open={openModal}
                         titleIcon={<CheckCircleRounded color={"success"}/>}
                         onClose={() => {
                             setOpenModal(false);
                             navigate("/salesmarketing/paorderslist")
                         }}/>
        </>
    )
}

export default PaOrdersCreateOrUpdatePage;